import { useState, useMemo, useCallback } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { filterTopics } from '../../utils/trainingUtils';
import { TopicStyle, TOPIC_STYLE_COLORS, TrainingTopic, PlanCategory } from '../../types/trainingTypes';
import { DeleteTopicModal } from './DeleteTopicModal';
import TopicContentViewerModal from './TopicContentViewerModal';
import { AssignToPlansModal } from './AssignToPlansModal';
import { BackButton } from '../ui';
import '../../css/MasterTopicListPage.css';

interface MasterTopicListPageProps {
    onBack: () => void;
    onOpenAddTopic: (topicId?: string) => void;
    onOpenImport: () => void;
    onOpenBulkAssign: (selectedTopicIds: string[]) => void;
    initialPlanFilter?: string;
}

type SortField = 'topicId' | 'style' | 'description' | 'timeValue' | 'categories' | 'scheduled';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 25;

type FilterTabValue = TopicStyle | 'All';

const FILTER_TABS: { label: string; value: FilterTabValue }[] = [
    { label: 'All', value: 'All' },
    { label: 'Video', value: 'Video' },
    { label: 'PDF', value: 'PDF' },
    { label: 'Poster', value: 'Poster' },
    { label: 'FatalGram', value: 'FatalGram' },
    { label: 'Uncategorized', value: 'Uncategorized' },
];

const STYLE_ICON_MAP: Record<TopicStyle, string> = {
    Video: '▶',
    PDF: '📄',
    Poster: '🖼',
    FatalGram: '⚠',
    Other: '📎',
    Uncategorized: '—',
};

const MasterTopicListPage = ({
    onBack,
    onOpenAddTopic,
    onOpenImport,
    onOpenBulkAssign,
    initialPlanFilter,
}: MasterTopicListPageProps) => {
    const { topics, schedules, plans, deleteTopic } = useSafetyTrainingContext();

    const [searchQuery, setSearchQuery] = useState('');
    const [styleFilter, setStyleFilter] = useState<FilterTabValue>('All');
    const [planFilter, setPlanFilter] = useState<string>(initialPlanFilter || '');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [sortField, setSortField] = useState<SortField>('topicId');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [previewTopic, setPreviewTopic] = useState<TrainingTopic | null>(null);
    const [assignTopic, setAssignTopic] = useState<TrainingTopic | null>(null);
    const [deleteTopicTarget, setDeleteTopicTarget] = useState<TrainingTopic | null>(null);

    // Build a map of planId -> parsed categories for resolving category names
    const categoryMap = useMemo(() => {
        const map: Record<string, PlanCategory> = {};
        plans.forEach(plan => {
            try {
                const cats: PlanCategory[] = JSON.parse(plan.categories);
                cats.forEach(c => { map[c.id] = c; });
            } catch { /* ignore parse errors */ }
        });
        return map;
    }, [plans]);

    // Count scheduled occurrences per topic
    const scheduledCountMap = useMemo(() => {
        const map: Record<string, number> = {};
        schedules.forEach(s => {
            map[s.topicId] = (map[s.topicId] || 0) + 1;
        });
        return map;
    }, [schedules]);

    // Set of topic IDs that are in an ACTIVE plan (assigned or scheduled) — protected from deletion
    const topicsInActivePlan = useMemo(() => {
        const activePlanIds = new Set(plans.filter(p => p.status === 'ACTIVE').map(p => p.id));
        const protectedTopicIds = new Set<string>();
        // Topics scheduled in active plans
        schedules.forEach(s => {
            if (activePlanIds.has(s.planId)) {
                protectedTopicIds.add(s.topicId);
            }
        });
        // Topics assigned to active plans (via planIds) even if not yet scheduled
        topics.forEach(t => {
            if (t.planIds && t.planIds.some(pid => activePlanIds.has(pid))) {
                protectedTopicIds.add(t.id);
            }
        });
        return protectedTopicIds;
    }, [plans, schedules, topics]);

    // Stat counts
    const statCounts = useMemo(() => ({
        total: topics.length,
        Video: topics.filter(t => t.style === 'Video').length,
        PDF: topics.filter(t => t.style === 'PDF').length,
        Poster: topics.filter(t => t.style === 'Poster').length,
        FatalGram: topics.filter(t => t.style === 'FatalGram').length,
    }), [topics]);

    // Filter tab counts
    const filterCounts = useMemo(() => {
        const counts: Record<string, number> = { All: topics.length };
        FILTER_TABS.forEach(tab => {
            if (tab.value !== 'All') {
                counts[tab.value] = topics.filter(t => t.style === tab.value).length;
            }
        });
        return counts;
    }, [topics]);

    // Filtered topics (search + style filter + plan filter)
    const filteredTopics = useMemo(() => {
        let result = filterTopics(topics, searchQuery, styleFilter as (TopicStyle | 'All'));
        if (planFilter === '__unassigned__') {
            result = result.filter(t => !t.planIds || t.planIds.length === 0);
        } else if (planFilter) {
            result = result.filter(t => t.planIds && t.planIds.includes(planFilter));
        }
        return result;
    }, [topics, searchQuery, styleFilter, planFilter]);

    // Natural sort collator (numeric-aware: "safety-2" before "safety-10")
    const naturalCollator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }), []);

    // Sorting
    const sortedTopics = useMemo(() => {
        const sorted = [...filteredTopics];
        sorted.sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'topicId':
                    cmp = naturalCollator.compare(a.topicId, b.topicId);
                    break;
                case 'style':
                    cmp = naturalCollator.compare(a.style, b.style);
                    break;
                case 'description':
                    cmp = naturalCollator.compare(a.description, b.description);
                    break;
                case 'timeValue':
                    cmp = a.timeValue - b.timeValue;
                    break;
                case 'categories':
                    cmp = (a.categoryIds?.length || 0) - (b.categoryIds?.length || 0);
                    break;
                case 'scheduled':
                    cmp = (scheduledCountMap[a.id] || 0) - (scheduledCountMap[b.id] || 0);
                    break;
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [filteredTopics, sortField, sortDirection, scheduledCountMap]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(sortedTopics.length / ITEMS_PER_PAGE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, sortedTopics.length);
    const pageTopics = sortedTopics.slice(startIndex, endIndex);

    // Handlers
    const handleSort = useCallback((field: SortField) => {
        if (field === sortField) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }, [sortField]);

    const handleFilterChange = useCallback((value: FilterTabValue) => {
        setStyleFilter(value);
        setCurrentPage(1);
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    }, []);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (selectedIds.size === pageTopics.length && pageTopics.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(pageTopics.map(t => t.id)));
        }
    }, [pageTopics, selectedIds.size]);

    const handleDelete = useCallback(async (topicId: string) => {
        const topic = topics.find(t => t.id === topicId);
        if (topic) {
            setDeleteTopicTarget(topic);
        }
    }, [topics]);

    const handleDeleteCompleted = useCallback(() => {
        if (deleteTopicTarget) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(deleteTopicTarget.id);
                return next;
            });
        }
        setDeleteTopicTarget(null);
    }, [deleteTopicTarget]);

    const handleBulkDelete = useCallback(async () => {
        if (selectedIds.size === 0) return;
        // Filter out topics that are scheduled in active plans
        const deletableIds = Array.from(selectedIds).filter(id => !topicsInActivePlan.has(id));
        const skippedCount = selectedIds.size - deletableIds.length;

        if (deletableIds.length === 0) {
            window.alert('All selected topics are scheduled in active plans and cannot be deleted.');
            return;
        }

        let message = `Delete ${deletableIds.length} selected topic(s)?`;
        if (skippedCount > 0) {
            message += `\n\n${skippedCount} topic(s) scheduled in active plans will be skipped.`;
        }

        if (window.confirm(message)) {
            for (const id of deletableIds) {
                await deleteTopic(id);
            }
            setSelectedIds(new Set());
        }
    }, [selectedIds, deleteTopic, topicsInActivePlan]);

    const getCategoryNames = (topic: TrainingTopic): string[] => {
        if (!topic.categoryIds || topic.categoryIds.length === 0) return [];
        return topic.categoryIds.map(id => categoryMap[id]?.name || id);
    };

    const renderSortIndicator = (field: SortField) => {
        if (sortField !== field) return <span className="mtl-sort-indicator">↕</span>;
        return (
            <span className="mtl-sort-indicator active">
                {sortDirection === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    const renderPageButtons = () => {
        const buttons: React.ReactNode[] = [];
        buttons.push(
            <button
                key="prev"
                className="mtl-page-btn"
                disabled={safeCurrentPage <= 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            >
                ‹
            </button>
        );
        for (let i = 1; i <= totalPages; i++) {
            if (totalPages <= 7 || i === 1 || i === totalPages || Math.abs(i - safeCurrentPage) <= 1) {
                buttons.push(
                    <button
                        key={i}
                        className={`mtl-page-btn ${i === safeCurrentPage ? 'active' : ''}`}
                        onClick={() => setCurrentPage(i)}
                    >
                        {i}
                    </button>
                );
            } else if (i === 2 && safeCurrentPage > 4) {
                buttons.push(<span key="dots-start" style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>);
            } else if (i === totalPages - 1 && safeCurrentPage < totalPages - 3) {
                buttons.push(<span key="dots-end" style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>);
            }
        }
        buttons.push(
            <button
                key="next"
                className="mtl-page-btn"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            >
                ›
            </button>
        );
        return buttons;
    };

    return (
        <div className="mtl-page">
            <BackButton onClick={onBack} buttonText="Back to Safety & Training Home" />

            {/* Header */}
            <div className="mtl-header">
                <div className="mtl-header-left">
                    <h2>Master Topic List</h2>
                </div>
                <div className="mtl-header-actions">
                    <button className="mtl-btn-import" onClick={onOpenImport}>Import</button>
                    <button className="mtl-btn-add" onClick={() => onOpenAddTopic()}>+ Add Topic</button>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="mtl-stat-cards">
                <div className="mtl-stat-card" style={{ borderLeftColor: '#3b82f6' }}>
                    <div className="mtl-stat-card-label">Total Topics</div>
                    <div className="mtl-stat-card-value">{statCounts.total}</div>
                </div>
                <div className="mtl-stat-card" style={{ borderLeftColor: TOPIC_STYLE_COLORS.Video }}>
                    <div className="mtl-stat-card-label">Videos</div>
                    <div className="mtl-stat-card-value">{statCounts.Video}</div>
                </div>
                <div className="mtl-stat-card" style={{ borderLeftColor: TOPIC_STYLE_COLORS.PDF }}>
                    <div className="mtl-stat-card-label">PDFs</div>
                    <div className="mtl-stat-card-value">{statCounts.PDF}</div>
                </div>
                <div className="mtl-stat-card" style={{ borderLeftColor: TOPIC_STYLE_COLORS.Poster }}>
                    <div className="mtl-stat-card-label">Posters</div>
                    <div className="mtl-stat-card-value">{statCounts.Poster}</div>
                </div>
                <div className="mtl-stat-card" style={{ borderLeftColor: TOPIC_STYLE_COLORS.FatalGram }}>
                    <div className="mtl-stat-card-label">FatalGrams</div>
                    <div className="mtl-stat-card-value">{statCounts.FatalGram}</div>
                </div>
            </div>

            {/* Filter Tabs + Search + Plan Filter */}
            <div className="mtl-filter-row">
                <div className="mtl-filter-tabs">
                    {FILTER_TABS.map(tab => (
                        <button
                            key={tab.value}
                            className={`mtl-filter-tab ${styleFilter === tab.value ? 'active' : ''}`}
                            onClick={() => handleFilterChange(tab.value)}
                        >
                            {tab.label}
                            <span className="mtl-filter-tab-count">{filterCounts[tab.value] || 0}</span>
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                        className="mtl-search-input"
                        style={{ width: 'auto', minWidth: 160, padding: '6px 10px' }}
                        value={planFilter}
                        onChange={e => { setPlanFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All Plans</option>
                        {plans.filter(p => p.status === 'DRAFT' || p.status === 'ACTIVE').map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                        <option value="__unassigned__">Unassigned</option>
                    </select>
                    <div className="mtl-search-wrapper">
                        <span className="mtl-search-icon">🔍</span>
                        <input
                            className="mtl-search-input"
                            type="text"
                            placeholder="Search by Topic ID or Description..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
                <div className="mtl-bulk-bar">
                    <span className="mtl-bulk-count">{selectedIds.size} selected</span>
                    <button
                        className="mtl-bulk-btn assign"
                        onClick={() => onOpenBulkAssign(Array.from(selectedIds))}
                    >
                        Assign to Category
                    </button>
                    <button className="mtl-bulk-btn delete" onClick={handleBulkDelete}>
                        Delete
                    </button>
                    <button className="mtl-bulk-btn clear" onClick={() => setSelectedIds(new Set())}>
                        Clear selection
                    </button>
                </div>
            )}

            {/* Table */}
            <div className="mtl-table-container">
                <table className="mtl-table">
                    <thead>
                        <tr>
                            <th className="no-sort" style={{ width: 40 }}>
                                <input
                                    type="checkbox"
                                    className="mtl-checkbox"
                                    checked={pageTopics.length > 0 && selectedIds.size === pageTopics.length}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th onClick={() => handleSort('topicId')}>
                                ID {renderSortIndicator('topicId')}
                            </th>
                            <th onClick={() => handleSort('style')}>
                                Style {renderSortIndicator('style')}
                            </th>
                            <th onClick={() => handleSort('description')}>
                                Description {renderSortIndicator('description')}
                            </th>
                            <th onClick={() => handleSort('timeValue')}>
                                Time Value {renderSortIndicator('timeValue')}
                            </th>
                            <th onClick={() => handleSort('categories')}>
                                Categories {renderSortIndicator('categories')}
                            </th>
                            <th onClick={() => handleSort('scheduled')}>
                                Scheduled {renderSortIndicator('scheduled')}
                            </th>
                            <th className="no-sort">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageTopics.length === 0 ? (
                            <tr>
                                <td colSpan={8}>
                                    <div className="mtl-empty-state">
                                        <p>No topics found. Add a topic or adjust your filters.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            pageTopics.map(topic => {
                                const catNames = getCategoryNames(topic);
                                const schedCount = scheduledCountMap[topic.id] || 0;
                                const isSelected = selectedIds.has(topic.id);
                                return (
                                    <tr key={topic.id} className={isSelected ? 'selected' : ''}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="mtl-checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleSelect(topic.id)}
                                            />
                                        </td>
                                        <td style={{ fontWeight: 600 }}>
                                            {topic.topicId}
                                            {topic.sourceLibraryTopicId && (
                                                <span style={{ fontSize: 9, background: '#ede9fe', color: '#6d28d9', padding: '1px 5px', borderRadius: 4, fontWeight: 600, marginLeft: 6 }}>
                                                    📥 Library
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className="mtl-style-badge"
                                                style={{ background: TOPIC_STYLE_COLORS[topic.style] || '#6b7280' }}
                                            >
                                                {STYLE_ICON_MAP[topic.style] || ''} {topic.style}
                                            </span>
                                        </td>
                                        <td>{topic.description}</td>
                                        <td>{topic.timeValue} min</td>
                                        <td>
                                            {catNames.length > 0 ? (
                                                <div className="mtl-categories">
                                                    {catNames.map((name, i) => (
                                                        <span key={i} className="mtl-category-pill">{name}</span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="mtl-no-categories">None</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="mtl-scheduled-count">
                                                {schedCount > 0 ? `${schedCount}x scheduled` : '—'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="mtl-action-icons">
                                                <button
                                                    className="mtl-action-icon"
                                                    title="Preview content"
                                                    onClick={() => setPreviewTopic(topic)}
                                                >
                                                    👁
                                                </button>
                                                <button
                                                    className="mtl-action-icon"
                                                    title="Assign to plans"
                                                    onClick={() => setAssignTopic(topic)}
                                                >
                                                    📋
                                                </button>
                                                <button
                                                    className="mtl-action-icon"
                                                    title="Edit topic"
                                                    onClick={() => onOpenAddTopic(topic.id)}
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    className="mtl-action-icon delete"
                                                    title="Delete topic"
                                                    onClick={() => handleDelete(topic.id)}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {sortedTopics.length > 0 && (
                    <div className="mtl-pagination">
                        <span className="mtl-pagination-info">
                            Showing {startIndex + 1}–{endIndex} of {sortedTopics.length} topics
                        </span>
                        <div className="mtl-pagination-buttons">
                            {renderPageButtons()}
                        </div>
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            <TopicContentViewerModal
                isOpen={!!previewTopic}
                onClose={() => setPreviewTopic(null)}
                topic={previewTopic}
            />

            {/* Assign to Plans Modal */}
            {assignTopic && (
                <AssignToPlansModal
                    isOpen={!!assignTopic}
                    onClose={() => setAssignTopic(null)}
                    topic={assignTopic}
                />
            )}

            {/* Delete Topic Modal */}
            <DeleteTopicModal
                isOpen={!!deleteTopicTarget}
                topic={deleteTopicTarget}
                onClose={() => setDeleteTopicTarget(null)}
                onDeleted={handleDeleteCompleted}
            />
        </div>
    );
};

export default MasterTopicListPage;
